<script setup>
import { ref, computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import FormInput from '../../../../../components/Form/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { isValidPassword } from 'shared/helpers/Validators';
import axios from 'axios';

const MIN_PASSWORD_LENGTH = 8;

const { t } = useI18n();

const isSignupInProgress = ref(false);

// 手机号注册数据
const phoneCredentials = reactive({
  phone: '',
  code: '',
  password: '',
  confirmPassword: '',
});

const countdown = ref(0);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const rules = {
  phoneCredentials: {
    phone: { required },
    code: { required },
    password: {
      required,
      isValidPassword,
      minLength: minLength(MIN_PASSWORD_LENGTH),
    },
    confirmPassword: { required, minLength: minLength(MIN_PASSWORD_LENGTH) },
  },
};

const v$Phone = useVuelidate({ phoneCredentials }, rules.phoneCredentials);

const globalConfig = computed(() => window.chatwootConfig || {});

const termsLink = computed(() =>
  t('REGISTER.TERMS_ACCEPT')
    .replace(
      'https://www.chatwoot.com/terms',
      globalConfig.value.termsURL || ''
    )
    .replace(
      'https://www.chatwoot.com/privacy-policy',
      globalConfig.value.privacyURL || ''
    )
);

// 检查表单是否有效
const isFormValid = computed(() => {
  return (
    !v$Phone.value.$invalid &&
    phoneCredentials.password === phoneCredentials.confirmPassword
  );
});

// 发送验证码
const sendCode = async () => {
  if (!phoneCredentials.phone) {
    useAlert(t('REGISTER.PHONE.PHONE_REQUIRED'));
    return;
  }

  try {
    await axios.post('/api/v1/phone_auth/send_code', {
      phone_number: phoneCredentials.phone,
    });

    // 开始倒计时
    countdown.value = 60;
    const timer = setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    useAlert(t('REGISTER.PHONE.CODE_SENT'));
  } catch (error) {
    useAlert(
      error.response?.data?.message || t('REGISTER.PHONE.SEND_CODE_FAILED')
    );
  }
};

// 手机号注册
const performPhoneRegistration = async () => {
  if (phoneCredentials.password !== phoneCredentials.confirmPassword) {
    useAlert(t('REGISTER.PHONE.PASSWORD_MISMATCH'));
    return;
  }

  isSignupInProgress.value = true;
  try {
    const response = await axios.post('/api/v1/phone_auth/login_or_register', {
      phone_number: phoneCredentials.phone,
      code: phoneCredentials.code,
      password: phoneCredentials.password,
      action: 'register',
    });

    useAlert(t('REGISTER.API.SUCCESS_MESSAGE'));
    setTimeout(() => {
      window.location = DEFAULT_REDIRECT_URL;
    }, 1000);
  } catch (error) {
    useAlert(error.response?.data?.message || t('REGISTER.API.ERROR_MESSAGE'));
  } finally {
    isSignupInProgress.value = false;
  }
};

const submit = () => {
  if (isSignupInProgress.value) return;

  v$Phone.value.$touch();
  if (v$Phone.value.$invalid) return;
  performPhoneRegistration();
};
</script>

<template>
  <div class="flex-1">
    <!-- 手机号注册表单 -->
    <form class="space-y-3" @submit.prevent="submit">
      <!-- 手机号 -->
      <FormInput
        v-model="phoneCredentials.phone"
        type="tel"
        name="phone_number"
        :label="$t('REGISTER.PHONE.PHONE_LABEL')"
        :placeholder="$t('REGISTER.PHONE.PHONE_PLACEHOLDER')"
        :has-error="v$Phone.phoneCredentials.phone.$error"
        @blur="v$Phone.phoneCredentials.phone.$touch"
      />

      <!-- 验证码 -->
      <div class="flex gap-2">
        <FormInput
          v-model="phoneCredentials.code"
          type="text"
          name="verification_code"
          class="flex-1"
          :label="$t('REGISTER.PHONE.CODE_LABEL')"
          :placeholder="$t('REGISTER.PHONE.CODE_PLACEHOLDER')"
          :has-error="v$Phone.phoneCredentials.code.$error"
          @blur="v$Phone.phoneCredentials.code.$touch"
        />
        <button
          type="button"
          :disabled="countdown > 0 || !phoneCredentials.phone"
          class="px-4 py-2 mt-6 text-sm font-medium text-white bg-n-brand rounded-md hover:bg-n-brand/80 disabled:bg-n-slate-3 disabled:cursor-not-allowed transition-colors min-w-[100px]"
          @click="sendCode"
        >
          {{ countdown > 0 ? `${countdown}s` : $t('REGISTER.PHONE.SEND_CODE') }}
        </button>
      </div>

      <!-- 密码 -->
      <div class="relative">
        <FormInput
          v-model="phoneCredentials.password"
          :type="showPassword ? 'text' : 'password'"
          name="phone_password"
          :label="$t('LOGIN.PASSWORD.LABEL')"
          :placeholder="$t('REGISTER.PHONE.PASSWORD_PLACEHOLDER')"
          :has-error="v$Phone.phoneCredentials.password.$error"
          @blur="v$Phone.phoneCredentials.password.$touch"
        />
        <button
          type="button"
          class="absolute right-3 top-9 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="showPassword = !showPassword"
        >
          {{ showPassword ? '隐藏' : '显示' }}
        </button>
      </div>

      <!-- 确认密码 -->
      <div class="relative">
        <FormInput
          v-model="phoneCredentials.confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          name="phone_confirm_password"
          :label="$t('REGISTER.PHONE.CONFIRM_PASSWORD_LABEL')"
          :placeholder="$t('REGISTER.PHONE.CONFIRM_PASSWORD_PLACEHOLDER')"
          :has-error="v$Phone.phoneCredentials.confirmPassword.$error"
          @blur="v$Phone.phoneCredentials.confirmPassword.$touch"
        />
        <button
          type="button"
          class="absolute right-3 top-9 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="showConfirmPassword = !showConfirmPassword"
        >
          {{ showConfirmPassword ? '隐藏' : '显示' }}
        </button>
      </div>

      <!-- 注册按钮 -->
      <NextButton
        lg
        type="submit"
        class="w-full font-medium"
        :label="$t('REGISTER.SUBMIT')"
        :disabled="isSignupInProgress || !isFormValid"
        :is-loading="isSignupInProgress"
      />
    </form>

    <!-- 用户协议 -->
    <p
      class="text-sm mt-5 mb-0 text-n-slate-11 [&>a]:text-n-blue-10 [&>a]:font-medium [&>a]:hover:text-n-blue-11"
      v-html="termsLink"
    />
  </div>
</template>
